/**
 * Reports API Hooks - For analytics and reporting
 *
 * Provides type-safe API operations for Reports with proper error handling,
 * caching, and validation following the established architecture pattern.
 */

import { useQuery } from "@tanstack/react-query"

import { usePermissions } from "@/hooks/usePermissions"
import { api } from "@/services/api"
import { unwrapStandardResponse } from "@/utils/apiResponse"

// Revenue report types
export interface RevenueSummary {
  total_billed: number
  total_collected: number
  total_outstanding: number
  collection_rate: number
  invoice_count: number
}

export interface RevenueByStatus {
  [status: string]: {
    count: number
    amount: number
  }
}

export interface RevenueByMethod {
  [method: string]: {
    count: number
    amount: number
  }
}

export interface RevenueReportResponse {
  period: string
  start_date: string
  end_date: string
  summary: RevenueSummary
  by_status: RevenueByStatus
  by_payment_method: RevenueByMethod
}

// Enrollment report types
export interface SessionEnrollment {
  session_id: string
  session_name: string
  enrollment_count: number
}

export interface EnrollmentReportResponse {
  total_enrollments: number
  by_status: { [status: string]: number }
  by_session: SessionEnrollment[]
  by_level: { [level: string]: number }
}

// Outstanding payments types
export interface OutstandingPaymentItem {
  invoice_id: string
  enrollment_id: string
  household_name: string
  household_email: string
  household_phone: string | null
  student_name: string
  class_name: string
  amount_due: number
  amount_paid: number
  outstanding_amount: number
  payment_status: string
  created_at: string | null
}

export interface OutstandingPaymentsResponse {
  items: OutstandingPaymentItem[]
  total_count: number
  total_outstanding: number
  skip: number
  limit: number
}

// Capacity report types
export interface CapacitySummary {
  total_classes: number
  total_capacity: number
  total_enrolled: number
  total_available: number
  overall_utilization: number
  full_classes: number
  near_full_classes: number
}

export interface ClassCapacity {
  class_id: string
  class_name: string
  swim_level: string
  day_of_week: string
  start_time: string
  max_students: number
  current_enrollment: number
  available_spots: number
  utilization_percent: number
  waitlist_count: number
  waitlist_enabled: boolean
  is_active: boolean
}

export interface CapacityReportResponse {
  summary: CapacitySummary
  classes: ClassCapacity[]
}

// Dashboard summary types
export interface DashboardSummary {
  students: {
    total: number
    households: number
  }
  enrollments: {
    active: number
    pending: number
  }
  payments: {
    outstanding_count: number
    outstanding_amount: number
    monthly_revenue: number
  }
  classes: {
    active: number
  }
}

export interface RevenueFilters {
  period?: "daily" | "weekly" | "monthly"
  start_date?: string
  end_date?: string
  session_id?: string
}

export interface OutstandingFilters {
  skip?: number
  limit?: number
  session_id?: string
  min_amount?: number
}

/**
 * Fetch revenue report
 */
export function useRevenueReport(filters: RevenueFilters = {}) {
  const { hasPermission } = usePermissions()

  return useQuery({
    queryKey: ["reports", "revenue", filters],
    queryFn: async (): Promise<RevenueReportResponse> => {
      const response = await api.instance.get("/api/v1/reports/revenue", {
        params: {
          period: filters.period ?? "monthly",
          start_date: filters.start_date,
          end_date: filters.end_date,
          session_id: filters.session_id,
        },
      })

      return unwrapStandardResponse<RevenueReportResponse>(
        response.data,
      ) as RevenueReportResponse
    },
    enabled:
      hasPermission("reports:read") ||
      hasPermission("reports:read_financial") ||
      hasPermission("system:admin"),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  })
}

/**
 * Fetch enrollment report
 */
export function useEnrollmentReport(sessionId?: string) {
  const { hasPermission } = usePermissions()

  return useQuery({
    queryKey: ["reports", "enrollments", sessionId],
    queryFn: async (): Promise<EnrollmentReportResponse> => {
      const response = await api.instance.get("/api/v1/reports/enrollments", {
        params: {
          session_id: sessionId,
        },
      })

      return unwrapStandardResponse<EnrollmentReportResponse>(
        response.data,
      ) as EnrollmentReportResponse
    },
    enabled: hasPermission("reports:read") || hasPermission("system:admin"),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  })
}

/**
 * Fetch outstanding payments report
 */
export function useOutstandingPayments(filters: OutstandingFilters = {}) {
  const { hasPermission } = usePermissions()

  return useQuery({
    queryKey: ["reports", "outstanding", filters],
    queryFn: async (): Promise<OutstandingPaymentsResponse> => {
      const response = await api.instance.get(
        "/api/v1/reports/payments/outstanding",
        {
          params: {
            skip: filters.skip ?? 0,
            limit: filters.limit ?? 50,
            session_id: filters.session_id,
            min_amount: filters.min_amount,
          },
        },
      )

      return unwrapStandardResponse<OutstandingPaymentsResponse>(
        response.data,
      ) as OutstandingPaymentsResponse
    },
    enabled:
      hasPermission("reports:read") ||
      hasPermission("reports:read_financial") ||
      hasPermission("system:admin"),
    staleTime: 2 * 60 * 1000, // 2 minutes (more frequently updated)
    gcTime: 5 * 60 * 1000,
  })
}

/**
 * Fetch capacity utilization report
 */
export function useCapacityReport(sessionId?: string, includeInactive = false) {
  const { hasPermission } = usePermissions()

  return useQuery({
    queryKey: ["reports", "capacity", sessionId, includeInactive],
    queryFn: async (): Promise<CapacityReportResponse> => {
      const response = await api.instance.get("/api/v1/reports/capacity", {
        params: {
          session_id: sessionId,
          include_inactive: includeInactive,
        },
      })

      return unwrapStandardResponse<CapacityReportResponse>(
        response.data,
      ) as CapacityReportResponse
    },
    enabled: hasPermission("reports:read") || hasPermission("system:admin"),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  })
}

/**
 * Fetch dashboard summary
 */
export function useDashboardSummary() {
  const { hasPermission } = usePermissions()

  return useQuery({
    queryKey: ["reports", "summary"],
    queryFn: async (): Promise<DashboardSummary> => {
      const response = await api.instance.get("/api/v1/reports/summary")

      return unwrapStandardResponse<DashboardSummary>(
        response.data,
      ) as DashboardSummary
    },
    enabled: hasPermission("reports:read") || hasPermission("system:admin"),
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000,
  })
}
